import React, { memo } from 'react';
import PropTypes from 'prop-types';
import '../../styles/LoadingState.css';

const toCssSize = (value) => {
  if (value === undefined || value === null || value === '') return undefined;
  return typeof value === 'number' ? `${value}px` : value;
};

const getLineWidths = ({ lines, widths, width }) => {
  if (Array.isArray(widths) && widths.length > 0) return widths;
  return Array.from({ length: Math.max(1, lines) }, () => width || '100%');
};

const LoadingState = memo(({
  as,
  variant = 'text',
  children,
  className = '',
  width,
  height,
  radius,
  gap,
  lines = 1,
  widths,
  label,
  announce = false,
  active = true,
  style,
  ...rest
}) => {
  const isText = variant === 'text';
  const isLines = variant === 'lines';
  const Element = as || (isLines ? 'div' : 'span');
  const classes = [
    'loading-state',
    `loading-state--${variant}`,
    active ? 'is-active' : 'is-static',
    className
  ].filter(Boolean).join(' ');
  const sharedStyle = {
    '--loading-state-width': toCssSize(width),
    '--loading-state-height': toCssSize(height),
    '--loading-state-radius': toCssSize(radius),
    '--loading-state-gap': toCssSize(gap),
    ...style
  };

  if (isText) {
    return (
      <Element
        className={classes}
        style={sharedStyle}
        role={announce ? 'status' : undefined}
        aria-live={announce ? 'polite' : undefined}
        aria-atomic={announce ? 'true' : undefined}
        aria-label={label || undefined}
        {...rest}
      >
        {children ?? label}
      </Element>
    );
  }

  if (isLines) {
    const lineWidths = getLineWidths({ lines, widths, width });

    return (
      <Element
        className={classes}
        style={sharedStyle}
        role={announce && label ? 'status' : undefined}
        aria-live={announce && label ? 'polite' : undefined}
        aria-label={announce && label ? label : undefined}
        aria-hidden={announce && label ? undefined : 'true'}
        {...rest}
      >
        {lineWidths.map((lineWidth, index) => (
          <span
            // Width is the stable identity for these presentation-only rows.
            key={`${String(lineWidth)}-${index}`}
            className="loading-state__line"
            style={{ '--loading-state-line-width': toCssSize(lineWidth) }}
          />
        ))}
      </Element>
    );
  }

  return (
    <Element
      className={classes}
      style={sharedStyle}
      role={announce && label ? 'status' : undefined}
      aria-live={announce && label ? 'polite' : undefined}
      aria-label={announce && label ? label : undefined}
      aria-hidden={announce && label ? undefined : 'true'}
      {...rest}
    />
  );
});

LoadingState.displayName = 'LoadingState';

LoadingState.propTypes = {
  as: PropTypes.elementType,
  variant: PropTypes.oneOf(['text', 'line', 'lines', 'pill', 'block']),
  children: PropTypes.node,
  className: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  radius: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  gap: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  lines: PropTypes.number,
  widths: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  label: PropTypes.string,
  announce: PropTypes.bool,
  active: PropTypes.bool,
  style: PropTypes.object
};

export default LoadingState;
